import XLSX from 'xlsx';
import eventBus from 'vue-option-events';
import store from '@/store';
import router from '@/router';
import reportServices from '@/services/report';
import pageServices from '@/services/page';
import ECM from '@/services/ecm';
import * as HANDLERS from '@/helpers/types/handlers';
import { reports } from '@/helpers/types/reports';
import { formatEntry, downloadFile } from '@/helpers/utils';
import { saveAs } from 'file-saver';

export const compareProps = (a, b) => Object.keys(a).every(key => b.hasOwnProperty(key));

export const compare = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const setActionsCondition = async (commit, page) => commit('setActionsCondition', page);

export const handleLoadRoute = async item => {
  if ('children' in item) return;

  const { id, page, mode, redirect, redirectParams, parameters, procedure } = item;

  await store.dispatch('page/clear');
  await eventBus.$emit(HANDLERS.CLOSE_DRAWER_DINAMIC);
  await eventBus.$emit(HANDLERS.CHILD_RELOAD);
  await store.dispatch('menu/setCurrentActiveMenu', id);
  await store.dispatch('page/setMain', page);
  await store.dispatch('page/clearFilter');
  await store.dispatch('breadcrumbs/setBreadcrumbs', { obj: item });

  if (redirect) {
    let pathRegex = /(http:\/\/)|(https:\/\/)/g;

    if (pathRegex.test(redirect)) {
      window.open(redirect, '_blank');
    } else if (router.history.current.name !== redirect) {
      router.push({ path: redirect });
    }

    return;
  }

  eventBus.$emit(HANDLERS.CLOSE_DRAWER_DINAMIC);

  await loadRoute({
    page,
    mode,
    redirectParams: parameters ? formatEntry(parameters) : redirectParams,
    procedure,
    target: 'page'
  });
};

export const loadRoute = async params => {
  const { origin, page, mode, target, rootLevel } = params;

  if (params.breadCrumbs) {
    await store.dispatch('breadcrumbs/setBreadcrumbs', { obj: params.breadCrumbs, dashboardNome: params.dashboardNome });
  }

  if (origin) {
    if ((origin !== page && target == 'page') || rootLevel) {
      await store.dispatch('page/setMain', page);
      store.dispatch('menu/setCurrentPage', {
        menu: page,
        mode
      });
    }
  } else {
    store.dispatch('menu/setCurrentPage', {
      menu: page,
      mode
    });
  }

  if (window.location.hash !== '#/page') {
    router.push({
      name: 'Page',
      params
    });
  } else {
    await store.dispatch('page/load', params);
  }
};

export const getListIndex = async (page, list, navigation, state) => {
  const keyList = state.schemes[page]['list'].attributes.attributesTable.keyValue;
  let result;

  list.filter((item, index) => {
    if (item[keyList] == state.entries[page][keyList]) result = navigation == 'previous' ? index - 1 : index + 1;
  });

  return result;
};

export const initLookups = async (commit, state, { mode, page, tab, step }) => {
  let form = {};

  if (tab) {
    form = state.schemes[page][mode].tabs.content[tab];
  } else if (step) {
    form = state.schemes[page][mode].steps.content[step];
  } else {
    if (mode == 'master') {
      form = state.schemes[page]['form'][mode];
    } else {
      form = state.schemes[page][mode];
    }
  }

  let fields = form.fields;

  if (mode !== 'master') form.groups.filter(group => group.fields.map(field => (fields = [...fields, field])));

  let newFields = [];

  for (let param of fields) {
    newFields.push({
      fill: param.fill,
      filledBy: param.filledBy,
      name: param.model,
      InvokeName: param.fieldName,
      procedure: 'procedure' in param ? param.procedure.name : '',
      typeLookup: 'typeLookup' in param ? param.typeLookup : null
    });
  }

  const { data } = await pageServices.initLookups({ model: state.models[page][mode], fields: newFields, page });

  for (let item of data.fields.filter(x => x.typeLookup && x.typeLookup !== 'conditional' && x.typeLookup !== 'simple')) {
    await commit('setLookup', { data: item, info: { mode, page, tab, step } });
  }

  await commit('updateModel', { page, mode, model: data.model });
};

export const initLookupsList = async (commit, state, { page, items }) => {
  if ('fields' in state.schemes[page].list) {
    let fields = state.schemes[page].list.fields;

    let newFields = [];

    if (!fields.length) return;

    for (let field of fields) {
      newFields.push({
        fill: field.fill,
        filledBy: field.filledBy,
        name: field.model,
        InvokeName: field.fieldName,
        procedure: 'procedure' in field ? field.procedure.name : '',
        typeLookup: 'typeLookup' in field ? field.typeLookup : null
      });
    }

    const { data } = await pageServices.initLookupsList({ model: items, fields: newFields, page });

    let model = [];

    for (let item of data) {
      let key = item.model[state.schemes[page]['list'].attributes.attributesTable.keyValue];
      await commit('setLookupListInit', { items: item.fields, page, key });
      model.push(item.model);
    }

    await commit('updateModelList', { page, mode: 'list', model });
  }
};

export const setLookupListScheme = async (commit, state, page) => {
  if ('fields' in state.schemes[page].list) {
    await commit('setLookupListScheme', {
      data: state.schemes[page].list.fields,
      info: {
        mode: 'list',
        page
      }
    });
  }
};

export const conditionEngine = async (commit, state, dispatch, { page, mode }) => {
  if (mode !== 'list') {
    if ('tabs' in state.schemes[page][mode] || 'steps' in state.schemes[page][mode]) return;

    await dispatch('conditions', { page, mode });
  }
};

export const lookupEngine = async (commit, state, dispatch, { page, mode }) => {
  if (mode !== 'list') {
    if ('tabs' in state.schemes[page][mode] || 'steps' in state.schemes[page][mode]) return;

    await initLookups(commit, state, { mode, page });
  }
};

export const showMessage = (commit, { message, color }) => {
  commit(
    'notification/showNotification',
    {
      color: color,
      text: message,
      showIconNotification: false,
      typeAlert: '2',
      typeNotification: '2'
    },
    { root: true }
  );
};

export const setPaths = async (scheme, rootState) => {
  if (scheme.background.desktopImage) {
    scheme.background.desktopImage = scheme.background.desktopImage.startsWith('static')
      ? scheme.background.desktopImage
      : rootState.layout.paths[scheme.background.desktopImage];
  }

  if (scheme.background.mobileImage) {
    scheme.background.mobileImage = scheme.background.mobileImage.startsWith('static')
      ? scheme.background.mobileImage
      : rootState.layout.paths[scheme.background.mobileImage];
  }

  return scheme;
};

export const checkParams = async (params, commit) => {
  if (!params.page) {
    const { currentPage, currentMode } = JSON.parse(sessionStorage.getItem('i4pro-portal')).menu;
    eventBus.$emit(HANDLERS.CHANGE_PAGE, { page: currentPage, mode: currentMode });
    params.page = currentPage;
    params.mode = currentMode;
    commit('setMain', currentPage);
  }

  return params;
};

export const keepMode = async target => {
  if (target) return target !== 'page';
  else true;
};

export const loadMode = async (state, commit, dispatch, params) => {
  let model;
  const { page, mode, defaultMode, parent, target, origin, redirectParams, line, lineModel } = params;
  let newMode = mode || state.schemes[page].default;

  if ('master' in state.schemes[page][newMode] && redirectParams) {
    params.master = JSON.parse(JSON.stringify(newMode));
    params.mode = 'list';
    newMode = 'list';
  }

  if ('steps' in state.schemes[page][mode]) {
    await commit('setModel', { data: {}, page, mode: defaultMode || newMode });
    await commit('setMode', { page, mode: defaultMode || newMode });

    if (redirectParams && Object.keys(redirectParams).length) {
      await commit('setEntry', { page, entry: redirectParams });
    }

    return;
  }

  let procedure = state.schemes[page][newMode].procedure;
  delete procedure.model;

  if (parent) {
    procedure.model = state.models[parent].master;
  } else if (state.main && page !== state.main && !(await keepMode(target))) {
    if (state.parents[page]) {
      procedure.model = state.models[state.parents[page]].master;
    }
  } else if ((await keepMode(target)) && page !== origin) {
    if (lineModel) {
      procedure.model = lineModel;
    } else {
      procedure.model = defaultMode === 'list' ? {} : state.models[origin][defaultMode];
    }
  }

  if (redirectParams) procedure.model = redirectParams;

  procedure.page = page;
  procedure.InvokeName = 'redirect';

  const { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

  if (data.invalid) {
    showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });
    return;
  }

  model = newMode === 'list' ? data.values : data.values[0];
  await commit('setModel', { data: model, page, mode: newMode });
  await commit('setMode', { page, mode: defaultMode || newMode });

  await dispatch('pageEvents', params);
};

export const loadPage = async (state, commit, dispatch, params, rootState) => {
  let model;
  const { page, defaultMode, parent, origin, redirectParams, lineModel, target } = params;
  let { data: dataScheme } = await pageServices.getSchema(page);

  dataScheme = await compatibilityMode(dataScheme);

  const scheme = { [page]: 'background' in dataScheme ? await setPaths(dataScheme, rootState) : dataScheme };
  commit('setInitialScheme', JSON.parse(JSON.stringify(scheme)));

  if ('master' in scheme[page][params.mode] && redirectParams) {
    params.master = JSON.parse(JSON.stringify(params.mode));
    params.mode = 'list';
  }

  scheme[page].default = params.mode;

  if ('steps' in scheme[page][scheme[page].default]) {
    if (redirectParams && Object.keys(redirectParams).length) await commit('setEntry', { page, entry: redirectParams });

    await commit('setScheme', scheme);

    return;
  }

  if (scheme[page][scheme[page].default].executeInLoad || scheme[page].default !== 'list') {
    const { procedure } = scheme[page][scheme[page].default];

    if (parent) procedure.model = state.models[parent].master;

    if (await keepMode(target)) {
      if (lineModel) procedure.model = lineModel;
      else procedure.model = defaultMode === 'list' ? {} : state.models[origin][defaultMode];
    }

    if (redirectParams) procedure.model = redirectParams;

    procedure.page = page;
    procedure.InvokeName = 'redirect';

    const { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

    if (data.invalid) {
      showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });
      return;
    }

    model = { [page]: { [scheme[page].default]: scheme[page].default === 'list' ? data.values : data.values[0] } };
  } else {
    model = { [page]: { [scheme[page].default]: scheme[page].default === 'list' ? [] : {} } };
  }

  await commit('setPage', { scheme, model });

  if (parent) await commit('setParent', { page, parent });

  await dispatch('pageEvents', params);
};

export const compatibilityMode = async scheme => {
  if ('list' in scheme) {
    if (!scheme.list.hasOwnProperty('recordActions')) {
      scheme.list.recordActionsModel = [];
      scheme.list.recordActions = {};
    }

    if (!scheme.list.conditions) {
      scheme.list.conditions = [];
    } else {
      for (const idx in scheme.list.conditions) {
        if ('readonly' in scheme.list.conditions[idx]) {
          continue;
        } else {
          scheme.list.conditions[idx]['readonly'] = {
            fields: []
          };
        }
      }
    }

    if (!scheme.list.inlineConditions) {
      scheme.list.inlineConditions = [];
    } else {
      for (const idx in scheme.list.inlineConditions) {
        if ('readonly' in scheme.list.inlineConditions[idx]) {
          continue;
        } else {
          scheme.list.inlineConditions[idx]['readonly'] = {
            fields: []
          };
        }
      }
    }
  }
  for (let item in scheme) {
    if (item !== 'list' && item !== 'title' && item !== 'breadcrumbs' && item !== 'background') {
      for (const idx in scheme[item].conditions) {
        if ('readonly' in scheme[item].conditions[idx]) {
          continue;
        } else {
          scheme[item].conditions[idx]['readonly'] = {
            fields: []
          };
        }
      }
    }
  }
  return scheme;
};

export const loadReportPage = async (state, commit, dispatch, params) => {
  const { data: dataScheme } = await pageServices.getSchemaProcedure({
    name: params.procedure,
    page: params.page,
    invokeName: 'redirect',
    model: params.redirectParams
  });

  const scheme = { [params.page]: dataScheme };

  scheme[params.page].default = params.mode;

  let procedure = {
    name: scheme[params.page][params.mode].procedure.name,
    page: params.page,
    invokeName: 'redirect',
    model: params.redirectParams
  };

  const { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

  const model = { [params.page]: { ['master']: data.values[0] } };

  await commit('setPage', { scheme, model });
};

export const loadDevPage = async (state, commit, dispatch, params, rootState) => {
  const scheme = { ['dev']: rootState.dev.schema };

  commit('setScheme', scheme);
  commit('setInitialScheme', JSON.parse(JSON.stringify(scheme)));

  let tableModel = [
    {
      name: 'Lorem01',
      calories: 159,
      carbs: 24
    },
    {
      name: 'Lorem02',
      calories: 237,
      carbs: 37
    },
    {
      name: 'Lorem03',
      calories: 262,
      carbs: 23
    }
  ];

  if (scheme['dev'][params.mode].procedure.name.length) {
    if ('master' in scheme['dev'][params.mode]) {
      scheme['dev'][params.mode].procedure.model = state.models['dev']['list'][0];

      const { data } = await pageServices.executeProcedureAction(addFilterParams(state, scheme['dev'][params.mode].procedure));
      if (data.invalid) {
        showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });

        return;
      }

      await commit('setModel', { page: 'dev', data: data.values[0], mode: 'master' });
    } else {
      const { data } = await pageServices.executeProcedureAction(addFilterParams(state, scheme['dev'][params.mode].procedure));

      if (data.invalid) {
        showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });

        return;
      }

      let model = '';
      if (params.mode === 'list') {
        model = data.values ? data.values : JSON.parse(JSON.stringify(tableModel));
      } else {
        model = data.values ? data.values[0] : {};
      }

      await commit('setModel', { page: 'dev', data: model, mode: params.mode });
    }

    await dispatch('pageEvents', params);
  }
};

export const loadMethod = async (state, params) => {
  let method;

  if (params.procedure) {
    method = 'eis';
  } else if (params.page.toString().toLowerCase() === 'dev') {
    method = 'dev';
  } else {
    method = state.schemes[params.page] ? 'mode' : 'page';
  }

  return {
    eis: loadReportPage,
    dev: loadDevPage,
    page: loadPage,
    mode: loadMode
  }[method];
};

export const addFilterParams = (state, procedure) => {
  if (!procedure.hasOwnProperty('model') && Object.entries(state.filter).length) {
    procedure.model = state.filter;
    return procedure;
  }

  if (procedure.hasOwnProperty('model') && !Object.entries(state.filter).length) {
    procedure.model = procedure.model;
    return procedure;
  }

  procedure.model = compare(procedure.model, state.filter) ? procedure.model : { ...state.filter, ...procedure.model };

  return procedure;
};

export const redirect = async (state, commit, params) => {
  const { page, mode, target, entry, dialog } = params.action.redirect;

  let routeParams = { page, mode, defaultMode: mode, origin: params.page, line: params.index, lineModel: params.model };

  if (entry) routeParams.redirectParams = formatEntry(entry);

  if (params.origin === 'dialog') eventBus.$emit(HANDLERS.CLOSE_DIALOG);

  if (params.origin === 'down') eventBus.$emit(HANDLERS.CLOSE_FORM_BELOW);

  if (routeParams.mode === 'list') delete state.entries[routeParams.page];

  if (page === state.main && mode === 'list') commit('clearFilter');

  routeParams.target = target;

  if (target !== 'page') {
    routeParams.defaultMode = state.schemes[params.page].default;
    await loadRoute(routeParams);

    if (target === 'sidebar') return;

    eventBus.$emit(params.mode === 'list' ? HANDLERS.RENDER_COMPONENT_IN_LIST : HANDLERS.RENDER_COMPONENT_IN_FORM, {
      context: mode,
      page,
      showIn: target,
      origin: params.page,
      dialog
    });

    return;
  }

  loadRoute(routeParams);
};

export const getSelectedItems = (state, page, model, initialModel) => {
  let newModel = JSON.parse(JSON.stringify(model));
  let newInitialModel = JSON.parse(JSON.stringify(initialModel));

  if ('attributesTable' in state.schemes[page]['list'].attributes) {
    if (state.schemes[page]['list'].attributes.attributesTable.dataTable.showSelect) {
      newModel = state.selectedItems.items;

      const selectedInitialItems = initialModel.filter(item =>
        state.selectedItems.items.find(x => {
          if (x[state.selectedItems.keyValue] === item[state.selectedItems.keyValue]) return item;
        })
      );

      newInitialModel = selectedInitialItems;
    }
  }

  return { model: newModel, initialModel: newInitialModel };
};

export const afterEvents = async (state, commit, dispatch, params, data, requestParams) => {
  const { update, page, tab, step, parameters, executeAlways, updateMaster } = params.action.after;

  // faz redirect somente no portal, no dev não faz!
  if (params.page === 'dev' && page) return;

  if (data.dictionaryReturns['@id_retorno'] < 0) {
    showMessage(commit, {
      message: data.dictionaryReturns['@ds_retorno'],
      color: data.dictionaryReturns['@message_class'] || 'error'
    });

    if (!executeAlways) return;
  } else if (data.dictionaryReturns['@id_retorno'] > 0) {
    showMessage(commit, {
      message: data.dictionaryReturns['@ds_retorno'],
      color: data.dictionaryReturns['@message_class'] || 'warning'
    });

    if (!executeAlways) return;
  } else {
    if (data.dictionaryReturns['@ds_retorno']) {
      showMessage(commit, {
        message: data.dictionaryReturns['@ds_retorno'],
        color: data.dictionaryReturns['@message_class'] || 'success'
      });
    }
  }

  if (updateMaster) {
    const masterPage = state.parents[params.page] ? state.parents[params.page] : params.page;
    const mode = state.schemes[masterPage].default;

    if ('master' in state.schemes[masterPage][mode]) {
      let procedure = state.schemes[masterPage][mode].procedure;
      delete procedure.model;
      procedure.model = state.entries[masterPage];
      procedure.invokeName = 'redirect';

      const { data: masterData } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

      if (masterData.invalid) {
        showMessage(commit, { message: masterData.dictionaryReturns['@ds_retorno'] });
        return;
      }

      await commit('setModel', { page: masterPage, data: masterData.values[0], mode, masterContent: true });

      if ('fields' in state.schemes[masterPage][mode].master) await initLookups(commit, state, { page: masterPage, mode: 'master' });

      if (!tab) eventBus.$emit('RELOAD_TAB');
    }
  }

  if (params.master) eventBus.$emit('RELOAD_TAB');

  if (update) {
    const targetMode = params.child ? state.schemes[params.page].default : params.mode;

    await commit('updateModel', {
      page: params.page,
      mode: targetMode,
      model: targetMode == 'list' ? data.values : data.values[0]
    });
    if (targetMode !== 'list') {
      if (compareProps(data.values[0], requestParams.model)) {
        await initLookups(commit, state, { mode: targetMode, page: params.page });
        await dispatch('conditions', { page: params.page, mode: targetMode });
      } else {
        showMessage(commit, {
          message:
            'Ao utilizar a funcionalidade de atualizar a page, os dados retornados na procedure devem ter as mesmas propriedades de quando a ação foi executada',
          color: 'error'
        });
        return;
      }
    }

    if (params.child && data.values.length === 1 && targetMode == 'list' && state.schemes[params.page].list.child.setAutomatically) {
      eventBus.$emit('RECEIVE_ENTRY', { page: params.page, entry: data.values[0] });
    }
  }

  if (tab) {
    eventBus.$emit('CHANGE_TAB', {
      tab: data.dictionaryReturns['@tab'] || parameters.tab,
      page: params.page,
      parentPage: state.parents[params.page],
      mode: params.mode
    });
    return;
  }

  if (step) {
    if (data.dictionaryReturns['@entry']) {
      await commit('setEntry', {
        page: params.page,
        entry: formatEntry(data.dictionaryReturns['@entry'])
      });

      eventBus.$emit('CHANGE_STEP', { step: data.dictionaryReturns['@step'] || parameters.step });
    } else {
      if (state.entries[params.page]) {
        eventBus.$emit('CHANGE_STEP', { step: data.dictionaryReturns['@step'] || parameters.step });
      } else {
        showMessage(commit, {
          message: 'Um contexto de navegação entre steps não foi definido no retorno da ação',
          color: 'error'
        });
        return;
      }
    }
  }

  if (page) {
    const { page, mode, rootLevel, keepDialog, keepSidebar } = parameters;

    if (params.origin === 'down') eventBus.$emit(HANDLERS.CLOSE_FORM_BELOW);
    !keepDialog && eventBus.$emit(HANDLERS.CLOSE_DIALOG, params.page);
    !keepSidebar && eventBus.$emit(HANDLERS.CLOSE_DRAWER_DINAMIC);

    //exemplo = "cd_apolice=123456;id_endosso=1;id_apolice=10; nm_segurado= 'Bozo'";
    const redirectParams = data.dictionaryReturns['@entry'] ? formatEntry(data.dictionaryReturns['@entry']) : {};

    await loadRoute({
      page: data.dictionaryReturns['@page'] || page,
      mode: data.dictionaryReturns['@mode'] || mode,
      origin: params.page,
      parent: state.parents[page],
      redirectParams: Object.entries(redirectParams).length ? redirectParams : undefined,
      target: params.origin === 'dialog' ? 'dialog' : 'page',
      rootLevel
    });
  }
};

export const downloadECM = async (action, model) => {
  const { domain, modelDocumentId } = action.ecm;

  const { data: key } = await ECM.GetKey(domain);

  if (modelDocumentId) {
    let hash = `${model[modelDocumentId].toString().padStart(9, '0')}${model[key]}`;

    const { data: document } = await ECM.DownloadDocument(hash);

    if (document) downloadFile(document.content, document.contentType, document.fileName.split('.').pop(), document.fileName.split('.').shift());
  } else {
    const { data: documents } = await ECM.GetDocuments(domain, model[key]);

    let hashs = documents.map(item => {
      let hash = item.id.toString().padStart(9, '0');
      return `${hash}${item.keyValues}`;
    });

    const { data: file } = await ECM.DownloadDocuments(hashs);

    if (file) downloadFile(file.content, file.contentType, file.fileName.split('.').pop(), file.fileName.split('.').shift());
  }
};

export const uploadEcm = async (state, params, rootState, mainMode, commit) => {
  const mode = mainMode ? mainMode : params.master ? 'master' : params.mode;
  let schema;

  if (params.tab && !('page' in state.schemes[params.page][mode].tabs.content[params.tab])) {
    schema = state.schemes[params.page][mode].tabs.content[params.tab];
  } else {
    schema = state.schemes[params.page][mode];
  }

  const fieldsGroups = schema.groups.reduce((result, item) => (result = [...result, ...item.fields.map(field => field)]), []);
  const fields = [...schema.fields, ...fieldsGroups].filter(x => x.ecm);
  const ecmReturn = mainMode ? {} : [];

  if (fields.length) {
    if (!mainMode) {
      const promises = fields.map(async field => {
        const { data: ecmkey } = await ECM.GetKey(field.ecm.domain);

        if (state.models[params.page][mode][field.model] && state.models[params.page][mode][field.model] instanceof File) {
          let document = {
            id_tp_document: field.ecm.id_tp_document,
            files: [state.models[params.page][mode][field.model]],
            dt_document: new Date()
          };

          const response = await ECM.InsertDocument(
            document,
            field.ecm.domain,
            state.models[params.page][mode][ecmkey],
            rootState.authentication.user.userName
          );

          if (response.status === 200) {
            ecmReturn.push({
              filename: state.models[params.page][mode][field.model].name,
              model: field.model,
              value: response.data,
              keyValue: state.models[params.page][mode][ecmkey]
            });
          }
        }
      });

      await Promise.all(promises);
    } else {
      const promises = fields.map(async field => {
        let items =
          'attributesTable' in state.schemes[params.page]['list'].attributes &&
          state.schemes[params.page]['list'].attributes.attributesTable.dataTable.showSelect
            ? state.selectedItems.items
            : state.models[params.page]['list'];

        const { data: ecmkey } = await ECM.GetKey(field.ecm.domain);

        const promisses = items.map(async (model, idx) => {
          if ('fileSend' in field.ecm && field.ecm.fileSend in model && model[field.ecm.fileSend] == 1) {
            showMessage(commit, {
              message: 'Arquivo já foi enviado, para o item selecionado ',
              color: 'error'
            });
          } else if (state.models[params.page][mode][field.model] && state.models[params.page][mode][field.model] instanceof File) {
            let document = {
              id_tp_document: field.ecm.id_tp_document,
              files: [state.models[params.page][mode][field.model]],
              dt_document: new Date()
            };

            const response = await ECM.InsertDocument(document, field.ecm.domain, model[ecmkey], rootState.authentication.user.userName);

            if (response.status === 200) {
              ecmReturn[idx] = {
                filename: state.models[params.page][mode][field.model].name,
                model: field.model,
                value: response.data,
                keyValue: model[ecmkey]
              };
            }
          }
        });

        await Promise.all(promisses);

        state.models[params.page][mode][field.model] = null;
      });

      await Promise.all(promises);
    }
  }

  return ecmReturn;
};

export const generatePdfReport = async (action, model) => {
  const { reportId, fileName } = action.report;
  const { data } = await reportServices.generateReport({ model, reportId, typeReport: reports.PDF });

  if (data) downloadFile(data.report, 'application/pdf', 'pdf', fileName);
};

export const generateTxtReport = async (action, model) => {
  const { reportId } = action.txt;
  const { data } = await reportServices.generateReport({ model, reportId, typeReport: reports.EXCEL });
  if (data[0].length)
    saveAs(
      new Blob(
        data[0].map(line => `${line[Object.keys(line)[0]]}\r\n`),
        { type: 'text/plain;charset=utf-8' }
      ),
      `${Date.now()}.txt`
    );
  else showMessage(commit, { message: 'Sem resultado' });
};

export const generateExcelReport = async (action, model) => {
  const { reportId } = action.excel;

  const { data } = await reportServices.generateReport({ model, reportId, typeReport: reports.EXCEL });

  if (data) downloadExcel(data.filter(x => x.length));
};

export const downloadExcel = data => {
  let hasTabs = false;
  let formated = [];
  let workbook = XLSX.utils.book_new();

  data.forEach(item => {
    if ('nr_tab' in item[0]) {
      hasTabs = true;
    }
  });

  if (hasTabs) {
    data.forEach(element => {
      const fields = [];
      element.forEach(line => {
        const { nr_tab, ...rest } = line;
        fields.push(rest);
      });

      const index = formated.findIndex(x => x.tab === element[0].nr_tab);

      if (index !== -1) formated[index].fields = [...formated[index].fields, [], ...fields];
      else formated.push({ tab: element[0].nr_tab, fields });
    });

    formated.forEach(element => {
      workbook.SheetNames.push(`Sheet${element.tab}`);
      workbook.Sheets[`Sheet${element.tab}`] = XLSX.utils.aoa_to_sheet(formatExcel(element.fields));
    });
  } else {
    workbook.SheetNames.push('Sheet1');
    data.forEach(element => formated.push(formatExcel(element)));
    workbook.Sheets['Sheet1'] = XLSX.utils.aoa_to_sheet([].concat(...formated));
  }

  XLSX.writeFile(workbook, `${Date.now()}.xlsx`);
};

export const formatExcel = items => {
  const formated = [];
  formated.push(Object.keys(items[0]));
  items.forEach(element => formated.push(Object.values(element)));
  formated.push([]);

  return formated;
};

export const sleep = async ms => {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
};
